<template>
  <div :class="['PlayerAvatar', { 'is-invited': isInvited, 'is-stacked': isStacked, 'is-overlapping': isOverlapping }]">
    <!-- Stacked seats reserve the badge row even when empty, so avatars in a row
         stay aligned instead of stepping up and down with their badges. -->
    <div v-if="isStacked" class="badge-slot">
      <div v-if="isUpForMeeting" class="fast"><GamIcon :name="GamIconName.FAST" is-current /></div>
    </div>

    <div
      :class="[
        'avatar',
        {
          me: player.username === userDetails?.username,
          'is-colorful': isColorful && !isInvited,
          'has-photo': !!player.profileImageBlobHash,
        },
      ]"
    >
      <div class="placeholder">
        <GamIcon :name="GamIconName.PAWN_EMPTY" size="calc(var(--card-avatar-size, 4rem) * 0.32)" is-current />
      </div>
      <img v-if="player.profileImageBlobHash" :src="imageSource" class="picture" />
    </div>

    <div v-if="isInvited && !isMyAvatar" class="invited-badge">
      <GamIcon :name="GamIconName.TIME" :size="GamIconSize.SMALL" is-current />
    </div>

    <!-- Top-left: freed NEW slot. Chess set sits on the avatar so Meet Anyways
         (above) and rating (below) keep their own bands. -->
    <div v-else-if="bringsChessSet" class="chess-set-badge">
      <GamIcon :name="GamIconName.CHESS_SET" :size="GamIconSize.SMALL" is-current />
    </div>

    <div v-else-if="isNew && !isMyAvatar" class="new-badge">NEW</div>

    <!-- Transparent to layout unless stacked, so the overlapping pill placement the
         shelf cards rely on is unchanged. -->
    <div v-if="!hideRating" class="rating-slot">
      <div
        v-if="player.rating"
        :class="['rating', { confirmed: !!props.isConfirmed, 'is-me': isMyAvatar, 'has-dots': isDots }]"
      >
        <GamIcon :name="GamIconName.GAMBIT" size="10px" is-current />
        <GamRatingDots v-if="isDots" :rating="player.rating" />
        <template v-else>{{ player.rating }}</template>
      </div>

      <!-- Falls back to the label while the player has no rating yet, so the seat
           is still identifiable as your own. -->
      <div v-else-if="isMyAvatar" class="rating you-badge"><span>YOU</span></div>
    </div>

    <div v-if="isUpForMeeting && !isStacked" class="fast"><GamIcon :name="GamIconName.FAST" is-current /></div>
  </div>
</template>

<script lang="ts" setup>
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { useAuthStore } from '@/stores/auth.store';
import GamIcon from '@/views/components/GamIcon.vue';
import GamRatingDots from '@/views/components/GamRatingDots.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import type { SparkPlayer } from '@/views/composables/models/main/Spark';
import { getBlobImage } from '@/views/main/sparks/utils/image-blob';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

interface PlayerAvatarProps {
  player: SparkPlayer;
  isConfirmed?: boolean;
  isUpForMeeting?: boolean;
  isColorful?: boolean;
  showNewBadge?: boolean;
  isInvited?: boolean;
  isStacked?: boolean;
  isOverlapping?: boolean;
  hideRating?: boolean;
}

const props = defineProps<PlayerAvatarProps>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const imageSource = computed(() => {
  if (!props.player.profileImageBlobHash) return;

  return getBlobImage(props.player.profileImageBlobHash);
});

const isMyAvatar = computed(() => props.player.username === userDetails.value?.username);

const isNew = computed(() => {
  if (!props.showNewBadge || isMyAvatar.value) return false;
  const partnerIds = userDetails.value?.handshakePartnerIds;
  if (!partnerIds || !props.player.id) return false;
  return !partnerIds.includes(props.player.id);
});

const isDots = computed(() => props.player.ratingDisplayMode === RatingDisplayMode.DOTS);

const bringsChessSet = computed(() => (props.player.chessSetCount ?? 0) > 0);
</script>

<style lang="scss" scoped>
.PlayerAvatar {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 5rem;
  height: 5rem;

  &.is-invited {
    opacity: 0.55;

    .avatar {
      box-shadow: none;
      border: 2px dashed var(--color-white-30);
    }

    .avatar img {
      filter: grayscale(1);
    }
  }

  /* Corner badges were sized for a 5rem wrapper around a 4rem avatar. Where the
     wrapper is shrunk to the avatar itself that slack is gone and the badges land
     on the face, so stacked mode lays them out vertically instead. */
  &.is-stacked {
    flex-direction: column;
    gap: 0.2rem;
    width: var(--card-avatar-size, 4rem);
    height: auto;
    /* Equal slots above and below keep the avatar on the row's centre line. Falls
       back when used outside a spark card, which hoists this token to size the seat. */
    --seat-slot: max(var(--card-badge-size, 1.25rem), 16px);

    /* Both shadows were separating these from the photo they used to overlap.
       In their own slots on a flat scrim they only smudge. */
    .avatar {
      transform: none;
      flex: none;
      box-shadow: none;
    }

    .rating {
      position: static;
      transform: none;
      box-shadow: none;
    }

    .rating-slot,
    .badge-slot {
      min-height: var(--seat-slot);
    }

    .rating-slot {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .invited-badge,
    .chess-set-badge,
    .new-badge {
      top: calc(var(--card-badge-size, 1.25rem) + 0.25rem);
    }
  }

  /* Confirmed piles overlap the circles. The gambit's one rating sits under the
     pile, so this wrapper is just the face. */
  &.is-overlapping {
    width: var(--card-avatar-size, 4rem);
    height: var(--card-avatar-size, 4rem);

    .avatar {
      transform: none;
      box-shadow: 0 0 0 2px #1c1c1c;
    }
  }

  /* A photo carries its own edge, but the placeholder is a dark disc on a dark
     card, so only a seat without a photo draws its outline. It sits on the
     placeholder because an inset shadow on the avatar paints under it. */
  &.is-stacked:not(.is-invited) .avatar:not(.has-photo) .placeholder,
  &.is-overlapping:not(.is-invited) .avatar:not(.has-photo) .placeholder {
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px var(--color-white-20);
  }
}

.rating-slot {
  display: contents;
}

.badge-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--card-badge-size, 1.25rem);

  .fast {
    position: static;
  }
}

.avatar {
  width: var(--card-avatar-size, 4rem);
  height: var(--card-avatar-size, 4rem);
  border-radius: 100%;
  box-shadow: 0px 0.5rem 1.25rem var(--color-dark-500);
  overflow: hidden;
  transform: translateY(-0.25rem);
  background: var(--color-dark-500);
  position: relative;
}

.invited-badge {
  position: absolute;
  top: 0.125rem;
  right: 0.125rem;
  width: var(--card-badge-size, 1.25rem);
  height: var(--card-badge-size, 1.25rem);
  border-radius: 50%;
  background: var(--color-dark-600);
  border: 1px solid var(--color-white-20);
  color: var(--color-white-60);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

/* Same corner the NEW badge used — free on spark seats, opposite the invited clock. */
.chess-set-badge {
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: var(--card-badge-size, 1.25rem);
  height: var(--card-badge-size, 1.25rem);
  border-radius: 50%;
  background: var(--color-dark-600);
  border: 1px solid var(--color-white-20);
  color: var(--color-white-80);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.new-badge {
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  background: var(--color-linear-gradient);
  color: var(--color-dark-700);
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 1px 4px;
  border-radius: 2rem;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(black, 0.4);
  z-index: 2;
}

.fast {
  background: var(--color-orange);
  color: var(--color-dark-900);
  width: var(--card-badge-size, 1.25rem);
  height: var(--card-badge-size, 1.25rem);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--card-badge-size, 1.25rem);
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
}

.avatar img,
.avatar .placeholder {
  width: 100%;
  height: 100%;
}

.avatar img {
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  filter: grayscale(1);
}

.avatar.is-colorful img {
  filter: grayscale(0);
}

.avatar .placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  color: var(--color-white);
}

.rating {
  background: linear-gradient(90deg, var(--color-white-90), var(--color-white-60)), var(--color-white);
  padding: 2px 6px;
  min-height: 16px;
  border-radius: 2rem;
  font-weight: bold;
  font-size: 10px;
  letter-spacing: 0.1em;
  position: absolute;
  bottom: 0.5rem;
  transform: translateY(50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  box-shadow: 0 3px 8px rgba(black, 0.5);
}

/* Dots are a row of squares on their own 3px rhythm, so the glyph needs a wider
   gap than the number does or it reads as one more dot. */
.rating.has-dots {
  gap: 5px;
}

.rating {
  background: var(--color-white-8);
  color: var(--color-white);
}

.rating.confirmed {
  background: var(--color-white-8);
  color: var(--color-white);
}

.rating.invited {
  background: var(--color-white-8);
  color: var(--color-white);
}

/* Your own seat carries the pink-tinted fill so it reads apart from the other
   seats, but keeps white content: the icon, dots and number stay legible where
   pink-on-pink washed out at this pill size. Declared after the confirmed/invited
   fills, which share its specificity. */
.rating.is-me,
.you-badge {
  background: var(--color-linear-gradient-30);
  color: var(--color-white);
}

.you-badge {
  padding: 2px 6px;
  min-height: 16px;

  span {
    background: var(--color-linear-gradient);
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}
</style>
